import { FormItem } from 'hic-components'
import { CountryFragment, MunicipalityFragment, useCountriesQuery } from 'src/api/api.generated'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, TextField } from '@vapor/material'
import CountryInput from './country-input'
import MunicipalityInput from './municipality-input'
import { Address } from './address-input.types'

export type AddressInputProps = {
  value?: Address | null
  onChange?: (value: Address | null) => void
  disabled?: boolean
  countryLabel?: string
  addressLabel?: string
  cityLabel?: string
  cityPlaceholder?: string
  cityTooltip?: string
  italianCityLabel?: string
  italianCityPlaceholder?: string
  italianCityTooltip?: string
  municipalityLabel?: string
  inColumns?: true
  withPlaceholders?: true
  defaultCountryIsoCode?: string
  hideAddress?: boolean
  fieldsSmall?: boolean
}

export default function AddressInput(props: AddressInputProps) {
  const {
    value,
    onChange,
    countryLabel,
    addressLabel,
    cityLabel,
    cityPlaceholder,
    cityTooltip,
    italianCityLabel,
    italianCityPlaceholder,
    italianCityTooltip,
    municipalityLabel,
    disabled,
    inColumns,
    withPlaceholders,
    defaultCountryIsoCode,
    hideAddress,
    fieldsSmall,
  } = props
  const { t } = useTranslation()
  const { data } = useCountriesQuery({ variables: { filter: { isoCode: { eq: 'IT' } } } })
  const italyCountryId = data?.countries?.[0]?.id

  const [localValue, setLocalValue] = useState<Address | null>(checkNullAddress(value || null))

  useEffect(() => {
    if (value !== undefined) {
      const checkedValue = checkNullAddress(value)
      if (areAddressesDifferent(checkedValue, localValue)) {
        setLocalValue(checkedValue)
      }
    }
  }, [value])

  const onLocalChange = useCallback(
    (newLocalValue: Address) => {
      const checkedNewLocalValue = checkNullAddress(newLocalValue)
      if (areAddressesDifferent(checkedNewLocalValue, localValue)) {
        setLocalValue(checkedNewLocalValue)
        onChange?.(checkedNewLocalValue)
      }
    },
    [localValue, onChange, setLocalValue],
  )

  const [localCountry, setLocalCountry] = useState<CountryFragment | { id: string } | null>(localValue?.countryId ? { id: localValue.countryId } : null)

  const isItaly = localCountry?.id === italyCountryId

  useEffect(() => {
    if (localCountry?.id !== localValue?.countryId) {
      setLocalCountry(localValue?.countryId ? { id: localValue.countryId, isoCode: localValue.countryIsoCode ?? undefined } : null)
    }
  }, [localValue?.countryId])

  const onCountryChange = useCallback(
    (country: CountryFragment | null) => {
      const italy = country?.isoCode === 'IT'
      setLocalCountry(country)
      const newLocalValue: Address = {
        countryId: country?.id,
        countryIsoCode: country?.isoCode,
        address: localValue?.address,
        city: localValue?.city,
        municipalityId: italy ? localValue?.municipalityId : null,
        municipalityName: italy ? localValue?.municipalityName : null,
        postalCode: italy ? localValue?.postalCode : null,
        province: italy ? localValue?.province : null,
      }
      onLocalChange(newLocalValue)
    },
    [onLocalChange, localValue],
  )

  const localMunicipality = useMemo(() => (localValue?.municipalityId ? { id: localValue?.municipalityId } : null), [localValue?.municipalityId])
  const onMunicipalityChange = useCallback(
    (municipality: MunicipalityFragment | null) => {
      if (municipality?.id === localValue?.municipalityId) {
        return
      }
      onLocalChange({
        countryId: localValue?.countryId,
        countryIsoCode: localValue?.countryIsoCode,
        address: localValue?.address,
        city: localValue?.city,
        municipalityId: municipality?.id,
        municipalityName: municipality?.name,
        province: municipality?.province,
        postalCode: municipality?.postalCode || localValue?.postalCode,
      })
    },
    [onLocalChange, localValue],
  )

  const onAddressChange = useCallback(
    (address: string) => {
      onLocalChange({
        countryId: localValue?.countryId,
        countryIsoCode: localValue?.countryIsoCode,
        address: address,
        municipalityId: localValue?.municipalityId,
        municipalityName: localValue?.municipalityName,
        city: localValue?.city,
        province: localValue?.province,
        postalCode: localValue?.postalCode,
      })
    },
    [onLocalChange, localValue],
  )

  const onCityChange = useCallback(
    (city: string) => {
      onLocalChange({
        countryId: localValue?.countryId,
        countryIsoCode: localValue?.countryIsoCode,
        address: localValue?.address,
        city: city,
        municipalityId: localValue?.municipalityId,
        municipalityName: localValue?.municipalityName,
        province: localValue?.province,
        postalCode: localValue?.postalCode,
      })
    },
    [onLocalChange, localValue],
  )

  const onPostalCodeChange = useCallback(
    (postalCode: string) => {
      onLocalChange({
        countryId: localValue?.countryId,
        countryIsoCode: localValue?.countryIsoCode,
        address: localValue?.address,
        city: localValue?.city,
        municipalityId: localValue?.municipalityId,
        municipalityName: localValue?.municipalityName,
        province: localValue?.province,
        postalCode: postalCode,
      })
    },
    [onLocalChange, localValue],
  )

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={inColumns ? 12 : 6}>
        <FormItem fieldLabel={countryLabel || t('commons.address.country')} fullWidth>
          <CountryInput //
            disabled={disabled}
            value={localCountry}
            onChange={onCountryChange}
            defaultValue={defaultCountryIsoCode}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
      <Grid item xs={12} md={inColumns ? 12 : 6}>
        {!hideAddress && (
          <FormItem fieldLabel={addressLabel || t('commons.address.address')} fullWidth>
            <TextField //
              disabled={disabled}
              value={localValue?.address || ''}
              onChange={(event) => onAddressChange(event.target.value)}
              size={fieldsSmall ? 'small' : undefined}
            />
          </FormItem>
        )}
      </Grid>
      {isItaly && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <FormItem fieldLabel={municipalityLabel || t('commons.address.municipality')} fullWidth>
                <MunicipalityInput //
                  disabled={disabled}
                  value={localMunicipality}
                  onChange={onMunicipalityChange}
                  size={fieldsSmall ? 'small' : undefined}
                />
              </FormItem>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormItem fieldLabel={t('commons.address.postal_code')} fullWidth>
                <TextField //
                  disabled={disabled}
                  value={localValue?.postalCode || ''}
                  onChange={(event) => onPostalCodeChange(event.target.value)}
                  size={fieldsSmall ? 'small' : undefined}
                />
              </FormItem>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormItem fieldLabel={t('commons.address.province')} fullWidth>
                <TextField disabled value={localValue?.province || ''} size={fieldsSmall ? 'small' : undefined} />
              </FormItem>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={6}>
        <FormItem
          fieldLabel={isItaly ? italianCityLabel || t('commons.address.italian_city') : cityLabel || t('commons.address.city')}
          fullWidth
          fieldTooltip={isItaly ? italianCityTooltip || t('commons.address.italian_city.tooltip') : cityTooltip || ''}
        >
          <TextField //
            disabled={disabled}
            value={localValue?.city || ''}
            placeholder={withPlaceholders ? (isItaly ? italianCityPlaceholder || t('commons.address.italian_city.placeholder') : cityPlaceholder || t('commons.address.city.placeholder')) : undefined}
            onChange={(event) => onCityChange(event.target.value)}
            size={fieldsSmall ? 'small' : undefined}
          />
        </FormItem>
      </Grid>
    </Grid>
  )
}

function areAddressesDifferent(address1: Address | null, address2: Address | null): boolean {
  return (
    address1?.address !== address2?.address ||
    address1?.city !== address2?.city ||
    address1?.countryId !== address2?.countryId ||
    address1?.municipalityId !== address2?.municipalityId ||
    address1?.postalCode !== address2?.postalCode ||
    address1?.province !== address2?.province
  )
}

function checkNullAddress(address: Address | null): Address | null {
  if (!address) return null
  if (address.address || address.city || address.countryId || address.countryIsoCode || address.municipalityId || address.postalCode || address.province) {
    return {
      address: address.address || null,
      city: address.city || null,
      countryId: address.countryId || null,
      countryIsoCode: address.countryIsoCode || null,
      municipalityId: address.municipalityId || null,
      municipalityName: address.municipalityName || null,
      postalCode: address.postalCode || null,
      province: address.province || null,
    }
  } else {
    return null
  }
}
